/**
 * @file UnknownDomainsList.tsx
 * @description «Неизвестные адреса за 7 дней» (phase19.28 этап А;
 * uiux-platform-shops §5 «Журнал неизвестных доменов», решение 10 —
 * шум скрывается).
 *
 * Адрес, на который приходили запросы, но он не привязан ни к одному
 * магазину: число запросов, последний день и «Привязать». IP-адреса
 * (запросы ботов) привязать нельзя — это шум: они свёрнуты одной строкой
 * «ещё N IP-адресов», а не идут вперемешку с доменами.
 */

import { Link2 } from "lucide-react";
import { useState } from "react";

import type { UnknownDomain } from "../../../lib/api-platform-shops";
import { pluralRu } from "../../../lib/plural";
import { isIpHost } from "../../../lib/platform/shop-domain-status";
import { formatPlatformDate } from "../../../lib/platform/shop-format";
import { Button } from "../../ui/button";
import { ListError } from "../../ui/ListStates";
import { Skeleton } from "../../ui/skeleton";

interface Props {
  items: UnknownDomain[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Подпись кнопки: «Привязать к магазину…» / «Добавить к магазину». */
  bindLabel: string;
  onBind: (item: UnknownDomain) => void;
}

export function UnknownDomainsList({ items, loading, error, onRetry, bindLabel, onBind }: Props) {
  const [showIps, setShowIps] = useState(false);
  if (error && items.length === 0) return <ListError message={error} onRetry={onRetry} />;
  if (loading && items.length === 0) return <Skeleton className="h-14 w-full" />;

  const domains = items.filter((item) => !isIpHost(item.host));
  const ips = items.filter((item) => isIpHost(item.host));
  if (domains.length === 0 && ips.length === 0) {
    return <p className="type-body text-on-surface-variant">Неизвестных адресов за 7 дней не было</p>;
  }
  const hits = (n: number) => `${n} ${pluralRu(n, ["запрос", "запроса", "запросов"])}`;

  return (
    <ul className="flex flex-col divide-y divide-[var(--border)]" aria-label="Неизвестные адреса">
      {domains.map((item) => (
        <li key={item.host} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="type-body break-all font-medium" title={item.host}>{item.hostUnicode || item.host}</span>
            <span className="type-caption text-on-surface-variant">
              {hits(item.hits)} · последний {formatPlatformDate(item.lastSeenDay)}
            </span>
          </span>
          <Button variant="soft" onClick={() => onBind(item)} className="self-start sm:self-auto">
            <Link2 className="size-4" aria-hidden />
            {bindLabel}
          </Button>
        </li>
      ))}
      {ips.length > 0 ? (
        <li className="flex flex-col gap-2 py-3">
          <Button variant="ghost" className="self-start" aria-expanded={showIps} onClick={() => setShowIps((v) => !v)}>
            {showIps ? "Скрыть" : "Показать"} {ips.length} {pluralRu(ips.length, ["IP-адрес", "IP-адреса", "IP-адресов"])} — привязать нельзя
          </Button>
          {showIps ? (
            <ul className="type-caption flex flex-col gap-1 text-on-surface-variant">
              {ips.map((item) => (
                <li key={item.host} className="break-all">
                  {item.host} · {hits(item.hits)}
                </li>
              ))}
            </ul>
          ) : null}
        </li>
      ) : null}
    </ul>
  );
}
